import React, { useMemo, useRef, Fragment } from "react";
import { get, noop } from "lodash";
import Parser from "html-react-parser";
import DOMPurify from "dompurify";
import ButtonLink from "@tvg/atomic-ui/_atom/Buttons/buttonLink";
import { useIsomorphicLayoutEffect } from "@tvg/custom-hooks";

import { Container, ContentButton } from "./styled-components";

const RegistrationLegalContent = ({
  content,
  contentType,
  isMobile,
  handleLegalContentButton,
  handleLegalContentLinks,
  validContentTypes
}) => {
  const scrollRef = useRef(null);

  useIsomorphicLayoutEffect(() => {
    if (contentType && scrollRef && scrollRef.current) {
      scrollRef.current.scrollIntoView();
    }
  }, [contentType]);

  // Anchor links to private policy or terms and conditions are
  // replaced within capi message to allow navigation within modal.
  const parseLegalContent = () => {
    // CAPI HTML string content is sanitized to prevent potential XSS
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
      // Sets target="_blank" for anchor elements containing rel="noopener" attribute
      if ("target" in node && node.hasAttribute("rel")) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener");
      }
    });

    const sanitizedContent = DOMPurify.sanitize(content);

    const options = {
      replace: (domNode) => {
        const buttonContentType = get(domNode, "attribs.data-internal");
        const className = get(domNode, "attribs.class");

        if (domNode.name === "a") {
          if (validContentTypes.includes(buttonContentType)) {
            return (
              <ContentButton
                qaLabel="legal-content-button"
                className={className}
                onClick={() =>
                  handleLegalContentButton({
                    isOpen: true,
                    contentType: buttonContentType
                  })
                }
              >
                {domNode.children[0].data}
              </ContentButton>
            );
          }

          if (
            !domNode.attribs.href.includes("#") &&
            !domNode.attribs.href.includes("mailto:") &&
            !domNode.attribs.href.includes("tel:")
          ) {
            return (
              <ButtonLink
                qaLabel="legal-content-link"
                url={domNode.attribs.href}
                tag="a"
                type="tertiary"
                size="autoSized"
                onClick={(event) =>
                  handleLegalContentLinks(event, domNode.attribs.href)
                }
              >
                {domNode.children[0].data}
              </ButtonLink>
            );
          }
        }
        return domNode;
      }
    };

    return (
      <Container isMobile={isMobile} ref={scrollRef}>
        {Parser(sanitizedContent, options)}
      </Container>
    );
  };

  const renderLegalContent = useMemo(() => parseLegalContent(), [contentType]);

  return <Fragment>{content && renderLegalContent}</Fragment>;
};

RegistrationLegalContent.defaultProps = {
  content: "",
  contentType: "",
  isMobile: false,
  handleLegalContentButton: noop,
  handleLegalContentLinks: noop,
  validContentTypes: []
};

export default RegistrationLegalContent;
